import { useEffect, useState } from 'react';
import { MOT_DE_PASSE_MIN, forceMotDePasse } from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { InputField } from '@/components/ui/field';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-client';
import { fermerSession } from '@/lib/session';
import { useChangerMotDePasse } from '../api/auth.api';

const VIDE = { actuel: '', nouveau: '', confirmation: '' };

/** Jauge indicative — la seule règle imposée reste la longueur. */
function Force({ motDePasse }: { motDePasse: string }) {
  if (motDePasse === '') return null;
  const { score, libelle } = forceMotDePasse(motDePasse);

  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className="flex flex-1 gap-1" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full ${i < score ? 'bg-mustard-500' : 'bg-line'}`}
          />
        ))}
      </div>
      <span className="text-xs text-ink-muted">{libelle}</span>
    </div>
  );
}

export function MotDePasseDialog({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const [form, setForm] = useState(VIDE);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const changer = useChangerMotDePasse();
  const toast = useToast();

  useEffect(() => {
    if (!ouvert) return;
    setForm(VIDE);
    setErreurs({});
  }, [ouvert]);

  function set<K extends keyof typeof VIDE>(cle: K, valeur: string) {
    setForm((f) => ({ ...f, [cle]: valeur }));
  }

  function valider(): boolean {
    const e: Record<string, string> = {};
    if (form.actuel === '') e.actuel = 'Le mot de passe actuel est obligatoire.';
    if (form.nouveau.length < MOT_DE_PASSE_MIN)
      e.nouveau = `Le nouveau mot de passe doit faire au moins ${MOT_DE_PASSE_MIN} caractères.`;
    else if (form.nouveau === form.actuel)
      e.nouveau = "Le nouveau mot de passe doit être différent de l'actuel.";
    if (form.confirmation !== form.nouveau)
      e.confirmation = 'Les deux mots de passe ne correspondent pas.';
    setErreurs(e);
    return Object.keys(e).length === 0;
  }

  async function soumettre() {
    if (!valider()) return;

    try {
      await changer.mutateAsync(form);
      toast.succes('Mot de passe modifié. Reconnectez-vous.');
      /* Le serveur a fermé toutes les sessions, y compris celle-ci : la garder
       * à l'écran donnerait une application qui échoue à chaque clic. */
      fermerSession();
      onFermer();
    } catch (e) {
      if (e instanceof ApiError) setErreurs(e.details ?? { actuel: e.message });
      else toast.erreur("Le mot de passe n'a pas pu être modifié.");
    }
  }

  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre="Changer le mot de passe"
      description="Toutes les sessions ouvertes seront fermées, celle-ci comprise."
      taille="sm"
      pied={
        <>
          <Button variant="outline" onClick={onFermer} disabled={changer.isPending}>
            Annuler
          </Button>
          <Button variant="primary" onClick={() => void soumettre()} chargement={changer.isPending}>
            Changer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InputField
          label="Mot de passe actuel"
          type="password"
          autoComplete="current-password"
          value={form.actuel}
          onChange={(e) => set('actuel', e.target.value)}
          erreur={erreurs.actuel}
          requis
        />

        <div>
          <InputField
            label="Nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            value={form.nouveau}
            onChange={(e) => set('nouveau', e.target.value)}
            erreur={erreurs.nouveau}
            aide={`${MOT_DE_PASSE_MIN} caractères minimum.`}
            requis
          />
          <Force motDePasse={form.nouveau} />
        </div>

        <InputField
          label="Confirmation"
          type="password"
          autoComplete="new-password"
          value={form.confirmation}
          onChange={(e) => set('confirmation', e.target.value)}
          erreur={erreurs.confirmation}
          requis
        />

        <Encart ton="neutre">
          Ce compte donne accès au fichier client complet : privilégiez une phrase longue plutôt
          qu'un mot compliqué.
        </Encart>
      </div>
    </Dialog>
  );
}
